import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NumberField } from "@/components/common";
import { CareEquipmentForm } from "@/components/CareEquipmentForm";
import { STATE_META, EVENT_ICON, DUE_META, careFileUrl } from "@/lib/care";
import { useLightbox } from "@/components/Lightbox";
import { WorkOrderDialog } from "@/components/WorkOrderDialog";
import { EquipmentSavPanel } from "@/components/care/EquipmentSavPanel";
import { WarrantySection } from "@/components/care/WarrantySection";
import { ANOMALY_FLAG } from "@/lib/sav";
import { fmtDate, fmtNum, todayIso } from "@/lib/helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

function StateBadge({ status }) {
  const m = STATE_META[status] || STATE_META.non_evalue;
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-medium ${m.cls}`} data-testid={`detail-state-${status}`}>
    <span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label}</span>;
}

function Field({ label, value }) {
  if (!value) return null;
  return <div><dt className="text-xs text-slate-400">{label}</dt><dd className="text-sm text-slate-700">{value}</dd></div>;
}

function ControlDialog({ open, onOpenChange, meta, isAdmin, onSubmit }) {
  const [f, setF] = useState({ date: todayIso(), control_type: "visuel", result_status: "", observations: "", action_todo: "", next_check_date: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ date: todayIso(), control_type: "visuel", result_status: "", observations: "", action_todo: "", next_check_date: "" }); }, [open]);
  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ ...f, next_check_date: f.next_check_date || null }); onOpenChange(false); }
    catch { /* garde le formulaire ouvert en cas d'échec */ } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="control-dialog">
        <DialogHeader><DialogTitle>Enregistrer un contrôle</DialogTitle>
          <DialogDescription>{isAdmin ? "Le contrôle sera validé et pilotera l'état affiché." : "Votre contrôle sera soumis à validation par un administrateur."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date du contrôle</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="control-date" /></div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={f.control_type} onValueChange={(v) => setF({ ...f, control_type: v })}>
                <SelectTrigger data-testid="control-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(meta?.control_types || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label>État retenu *</Label>
            <Select value={f.result_status} onValueChange={(v) => setF({ ...f, result_status: v })}>
              <SelectTrigger data-testid="control-result"><SelectValue placeholder="Choisir l'état observé" /></SelectTrigger>
              <SelectContent>{Object.entries(meta?.states || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-slate-400">« Bon état apparent » est un constat visuel, pas une garantie de fonctionnement.</p></div>
          <div className="space-y-1.5"><Label>Observations</Label><Textarea value={f.observations} onChange={(e) => setF({ ...f, observations: e.target.value })} data-testid="control-observations" /></div>
          <div className="space-y-1.5"><Label>Action à prévoir</Label><Input value={f.action_todo} onChange={(e) => setF({ ...f, action_todo: e.target.value })} data-testid="control-action" /></div>
          <div className="space-y-1.5"><Label>Prochaine échéance (facultatif)</Label><Input type="date" value={f.next_check_date} onChange={(e) => setF({ ...f, next_check_date: e.target.value })} data-testid="control-next-check" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={!f.result_status || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="control-submit">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le contrôle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventDialog({ open, onOpenChange, meta, isAdmin, onSubmit }) {
  const TYPES = isAdmin ? ["signalement", "entretien", "reparation", "retrait"] : ["signalement"];
  const [f, setF] = useState({ type: "signalement", date: todayIso(), description: "", work_link: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ type: TYPES[0], date: todayIso(), description: "", work_link: "" }); }, [open]); // eslint-disable-line
  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ ...f, work_link: f.work_link || null }); onOpenChange(false); }
    catch { /* conserve le formulaire */ } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-dialog">
        <DialogHeader><DialogTitle>Ajouter une intervention</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{meta?.event_types?.[t] || t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="event-date" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="event-description" /></div>
          <div className="space-y-1.5"><Label>Lien fiche Travaux (facultatif)</Label><Input value={f.work_link} onChange={(e) => setF({ ...f, work_link: e.target.value })} placeholder="Référence visite / chantier" data-testid="event-worklink" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="event-submit">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CareDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("care.manage");
  const canControl = hasPerm("care.control");
  const [meta, setMeta] = useState(null);
  const [eq, setEq] = useState(null);
  const [error, setError] = useState(false);
  const [openControl, setOpenControl] = useState(false);
  const [openEvent, setOpenEvent] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openReplace, setOpenReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/care/meta").then((r) => setMeta(r.data)); }, []);
  const lb = useLightbox();
  const load = useCallback(() => {
    setError(false);
    api.get(`/care/equipment/${id}`).then((r) => setEq(r.data)).catch(() => setError(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addControl = async (payload) => {
    try { const { data } = await api.post(`/care/equipment/${id}/controls`, payload); toast.success(data.validated ? "Contrôle enregistré et validé" : "Contrôle soumis pour validation"); load(); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };
  const addEvent = async (payload) => {
    try { await api.post(`/care/equipment/${id}/events`, payload); toast.success("Intervention enregistrée"); load(); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };
  const validateControl = async (evId) => {
    try { await api.post(`/care/controls/${evId}/validate`); toast.success("Contrôle validé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const togglePublish = async (ev) => {
    try { await api.post(`/care/events/${ev.id}/client-publish`, { published: !ev.client_published }); toast.success(ev.client_published ? "Retiré de l'espace client" : "Publié dans l'espace client"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async (payload) => {
    try { await api.put(`/care/equipment/${id}`, payload); toast.success("Fiche mise à jour"); setOpenEdit(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async () => {
    try { await api.post(`/care/equipment/${id}/archive`); toast.success("Équipement archivé"); navigate("/care"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onFiles = async (e, kind) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", kind);
      try { await api.post(`/care/equipment/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(apiError(err)); }
    }
    setUploading(false); e.target.value = ""; toast.success("Fichier(s) ajouté(s)"); load();
  };
  const deleteFile = async (fid) => {
    try { await api.delete(`/care/files/${fid}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return (
    <div className="space-y-4" data-testid="care-detail-error">
      <button onClick={() => navigate("/care")} className="flex items-center gap-1 text-sm text-slate-500"><Icons.ChevronLeft className="h-4 w-4" /> SIGNA CARE</button>
      <Card className="p-6 text-center text-sm text-rose-600">Impossible de charger cette fiche. Réessayez plus tard.</Card>
    </div>
  );
  if (!eq) return <div className="py-16 text-center text-slate-400" data-testid="care-detail-loading">Chargement…</div>;

  const CI = Icons[EVENT_ICON.controle];
  const pendingControls = (eq.events || []).filter((e) => e.type === "controle" && !e.validated);

  return (
    <div className="space-y-5" data-testid="care-detail-page">
      <button onClick={() => navigate("/care")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="care-back">
        <Icons.ChevronLeft className="h-4 w-4" /> SIGNA CARE
      </button>

      {eq.is_demo && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="care-demo-flag"><Icons.FlaskConical className="mr-1.5 inline h-3.5 w-3.5" /> Donnée de DÉMONSTRATION (fictive).</div>}
      {eq.replaced_by_info && <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600" data-testid="care-replaced-by">Remplacé par <button className="font-medium text-emerald-700 underline" onClick={() => navigate(`/care/${eq.replaced_by_info.id}`)}>{eq.replaced_by_info.reference} · {eq.replaced_by_info.name}</button></div>}
      {eq.replaces_info && <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600" data-testid="care-replaces">Remplace <button className="font-medium text-emerald-700 underline" onClick={() => navigate(`/care/${eq.replaces_info.id}`)}>{eq.replaces_info.reference} · {eq.replaces_info.name}</button></div>}

      {/* En-tête fiche */}
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono-data text-xs text-slate-400">{eq.reference}</span>
              <StateBadge status={eq.status} />
              {eq.due_status && DUE_META[eq.due_status] && <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${DUE_META[eq.due_status].cls}`}>{DUE_META[eq.due_status].label}</span>}
              {eq.open_anomalies > 0 && <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700" data-testid="care-anomaly-flag">{ANOMALY_FLAG}</span>}
            </div>
            <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">{eq.name}</h1>
            <p className="text-sm text-slate-500">{eq.category_label} · {eq.client_name} — {eq.site_name}</p>
            <p className="mt-1 text-sm text-slate-500">{[eq.zone, eq.location_desc].filter(Boolean).join(" · ") || "Emplacement non précisé"}</p>
            {eq.mode === "ensemble" && <p className="mt-1 inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Ensemble suivi : {fmtNum(eq.quantity)} {meta?.units?.[eq.unit] || eq.unit}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {canControl && <WorkOrderDialog scope="equipment" equipmentId={eq.id} defaultDue={eq.next_check_date || ""} />}
            {canManage && (
              <>
                <Button variant="outline" size="sm" onClick={() => setOpenEdit(true)} data-testid="care-edit-btn"><Icons.Pencil className="mr-1.5 h-4 w-4" />Modifier</Button>
                <Button variant="outline" size="sm" onClick={() => setOpenReplace(true)} data-testid="care-replace-btn"><Icons.RefreshCw className="mr-1.5 h-4 w-4" />Remplacer</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-rose-600" data-testid="care-archive-btn"><Icons.Archive className="mr-1.5 h-4 w-4" />Archiver</Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Archiver cet équipement ?</AlertDialogTitle>
                      <AlertDialogDescription>La fiche et son historique sont conservés ; elle n'apparaîtra plus dans le registre actif.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={archive} className="bg-rose-600 hover:bg-rose-700" data-testid="care-archive-confirm">Archiver</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-slate-100 pt-4 sm:grid-cols-3">
          <Field label="Fabricant" value={eq.manufacturer} />
          <Field label="Modèle" value={eq.model} />
          <Field label="N° de série" value={eq.serial} />
          <Field label="Date de pose" value={eq.install_date ? fmtDate(eq.install_date) : null} />
          <Field label="Entreprise de pose" value={eq.installer} />
          <Field label="Nature du support" value={eq.support_nature} />
          <Field label="Mode de fixation" value={eq.fixation_mode} />
          <Field label="Dernier contrôle" value={eq.last_control_date ? fmtDate(eq.last_control_date) : "Aucun contrôle enregistré"} />
          <Field label="Prochaine échéance" value={eq.next_check_date ? fmtDate(eq.next_check_date) : null} />
        </dl>
        {eq.internal_notes && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600" data-testid="care-notes"><span className="text-xs font-semibold text-slate-400">Notes internes · </span>{eq.internal_notes}</p>}
      </Card>

      {/* Actions contrôle / intervention */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setOpenControl(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="care-add-control"><Icons.ClipboardCheck className="mr-1.5 h-4 w-4" />Enregistrer un contrôle</Button>
        <Button variant="outline" onClick={() => setOpenEvent(true)} data-testid="care-add-event"><Icons.TriangleAlert className="mr-1.5 h-4 w-4" />Ajouter une intervention</Button>
      </div>

      {pendingControls.length > 0 && canManage && (
        <Card className="border-amber-200 bg-amber-50/50 p-4" data-testid="care-pending-controls">
          <p className="mb-2 text-sm font-semibold text-amber-800">{pendingControls.length} contrôle(s) en attente de validation</p>
          <ul className="space-y-2">
            {pendingControls.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-white p-2.5 text-sm">
                <span>{fmtDate(c.date)} · {c.result_status_label} · {c.author_name}</span>
                <Button size="sm" onClick={() => validateControl(c.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`care-validate-${c.id}`}>Valider</Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <EquipmentSavPanel eq={eq} canManage={canManage} />
      <WarrantySection equipmentId={eq.id} canManage={canManage} />

      {/* Photos & documents */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-[#0D2E24]">Photos & documents</h2>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e, "photo")} data-testid="care-photo-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="care-add-photo">
              {uploading ? <Icons.Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Icons.Camera className="mr-1.5 h-4 w-4" />}Ajouter une photo
            </Button>
          </div>
        </div>
        {(eq.photos?.length || 0) === 0 ? (
          <p className="mt-3 text-sm text-slate-400">Aucune photo.</p>
        ) : (
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {eq.photos.map((p, idx) => (
              <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200" data-testid={`care-photo-${p.id}`}>
                <img src={careFileUrl(p.id)} alt={p.caption || ""} onClick={() => lb.open(eq.photos.map((x) => ({ src: careFileUrl(x.id), caption: x.caption })), idx)} className="h-full w-full cursor-zoom-in object-cover" />
                <button onClick={() => deleteFile(p.id)} className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`care-photo-del-${p.id}`}><Icons.X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
      {lb.node}

      {/* Chronologie */}
      <Card className="p-4">
        <h2 className="mb-3 font-display text-base font-semibold text-[#0D2E24]">Historique</h2>
        {(eq.events?.length || 0) === 0 ? (
          <p className="text-sm text-slate-400">Aucun évènement enregistré.</p>
        ) : (
          <ul className="space-y-4">
            {eq.events.map((ev) => {
              const EI = Icons[EVENT_ICON[ev.type] || "Circle"];
              return (
                <li key={ev.id} className="flex gap-3" data-testid={`care-event-${ev.id}`}>
                  <div className="flex flex-col items-center">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${ev.type === "controle" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}><EI className="h-4 w-4" /></span>
                    <span className="mt-1 w-px flex-1 bg-slate-100" />
                  </div>
                  <div className="min-w-0 flex-1 pb-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{ev.type_label}</span>
                      {ev.type === "controle" && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">{ev.result_status_label}</span>}
                      {ev.type === "controle" && !ev.validated && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700">En attente</span>}
                      <span className="text-xs text-slate-400">{fmtDate(ev.date)} · {ev.author_name}</span>
                      {ev.client_published && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700" data-testid={`care-event-published-${ev.id}`}>Publié au client</span>}
                      {canManage && (ev.type !== "controle" || ev.validated) && (
                        <button onClick={() => togglePublish(ev)} className="text-[11px] text-slate-500 underline hover:text-slate-700" data-testid={`care-event-publish-${ev.id}`}>{ev.client_published ? "Retirer" : "Publier au client"}</button>)}
                    </div>
                    {ev.type === "controle" && <p className="text-xs text-slate-400">{ev.control_type_label}{ev.action_todo ? ` · Action : ${ev.action_todo}` : ""}{ev.next_check_date ? ` · Échéance : ${fmtDate(ev.next_check_date)}` : ""}</p>}
                    {ev.description && <p className="mt-0.5 text-sm text-slate-600">{ev.description}</p>}
                    {ev.work_link && <p className="text-xs text-slate-400">Fiche Travaux : {ev.work_link}</p>}
                    {(ev.photos?.length || 0) > 0 && (
                      <div className="mt-1.5 flex gap-1.5">{ev.photos.map((p, idx) => <img key={p.id} src={careFileUrl(p.id)} alt="" onClick={() => lb.open(ev.photos.map((x) => ({ src: careFileUrl(x.id), caption: x.caption })), idx)} className="h-14 w-14 cursor-zoom-in rounded object-cover" />)}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <ControlDialog open={openControl} onOpenChange={setOpenControl} meta={meta} isAdmin={canManage} onSubmit={addControl} />
      <EventDialog open={openEvent} onOpenChange={setOpenEvent} meta={meta} isAdmin={canManage} onSubmit={addEvent} />
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Modifier la fiche</DialogTitle></DialogHeader>
          {meta && <CareEquipmentForm meta={meta} initial={eq} lockContext onSave={saveEdit} onCancel={() => setOpenEdit(false)} testPrefix="edit-eq" />}</DialogContent>
      </Dialog>
      <ReplaceDialog open={openReplace} onOpenChange={setOpenReplace} meta={meta} eq={eq} onDone={(newId) => { setOpenReplace(false); navigate(`/care/${newId}`); }} />
    </div>
  );
}

function ReplaceDialog({ open, onOpenChange, meta, eq, onDone }) {
  const replace = async (payload) => {
    try {
      const { data } = await api.post(`/care/equipment/${eq.id}/replace`, { new: payload, description: "Remplacement enregistré" });
      toast.success("Équipement remplacé — ancienne fiche conservée");
      onDone(data.new_id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const prefill = eq ? { client_id: eq.client_id, site_id: eq.site_id, category: eq.category, name: eq.name, zone: eq.zone, mode: eq.mode, unit: eq.unit, quantity: eq.mode === "ensemble" ? eq.quantity : null } : {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="replace-dialog">
        <DialogHeader><DialogTitle>Remplacer l'équipement</DialogTitle>
          <DialogDescription>L'ancienne fiche est conservée avec son historique et reliée au nouvel équipement.</DialogDescription></DialogHeader>
        {meta && <CareEquipmentForm meta={meta} initial={prefill} lockContext onSave={replace} onCancel={() => onOpenChange(false)} testPrefix="replace-eq" />}
      </DialogContent>
    </Dialog>
  );
}
